// ===========================================
// Conceptos repartibles de un cliente — ORR-230 fase 3
// ===========================================

import type { VencimientoConCliente } from '@/types/vencimientos'
import type { TipoConcepto } from '@/core/services/conceptosService'

export interface ConceptoDelCliente {
  tipo: TipoConcepto
  id: string
  nombre: string
}

export const claveConcepto = (tipo: TipoConcepto, id: string): string => `${tipo}:${id}`

/**
 * Deriva los conceptos que se pueden repartir a partir de los vencimientos del cliente.
 *
 * La fuente son los vencimientos y no `impuestos_cliente`: la planilla también tiene filas
 * ancladas en una obligación recurrente, y una de las cuatro obligaciones resuelve por filtros
 * dinámicos, así que la lista de conceptos de un cliente no se puede armar leyendo su lista de
 * impuestos. Lo que ya está en la vista es exactamente lo que hay para repartir.
 *
 * Las filas sin ancla se descartan en vez de mostrarse sin poder asignarse: son las 3 de
 * "Balances" que la fase 0 no pudo normalizar, y un concepto que no se puede guardar en
 * `cliente_responsable_conceptos` sería un control que no hace nada.
 */
export const derivarConceptos = (vencimientos: VencimientoConCliente[]): ConceptoDelCliente[] => {
  const porClave = new Map<string, ConceptoDelCliente>()

  for (const v of vencimientos) {
    const tipo: TipoConcepto = v.impuesto_id ? 'impuesto' : 'obligacion'
    const id = v.impuesto_id ?? v.obligacion_recurrente_id
    // El nombre corto solo existe cuando hay impuesto de catálogo; las obligaciones
    // recurrentes traen su nombre en `tipo_impuesto`.
    const nombre = v.impuesto_nombre_corto ?? v.tipo_impuesto

    if (!id || !nombre) continue

    const clave = claveConcepto(tipo, id)
    if (!porClave.has(clave)) porClave.set(clave, { tipo, id, nombre })
  }

  return [...porClave.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
}

export interface OpcionReparto {
  id: string
  nombre: string
  /** false cuando la persona ya no es responsable del cliente pero conserva asignaciones. */
  vigente: boolean
}

/**
 * Quiénes pueden marcarse para un concepto.
 *
 * Son los responsables actuales del cliente **más** cualquiera que conserve una asignación sin
 * serlo ya. Ese segundo caso existe porque quitarle la responsabilidad a alguien
 * (`clientesService.sincronizarResponsables`) reemplaza `cliente_responsables` y no toca sus filas
 * de reparto: sin incluirlo acá, sus asignaciones quedan clavadas sin forma de desmarcarlas, y el
 * concepto sigue a cargo de alguien que ya no atiende al cliente.
 */
export const opcionesDeReparto = (
  responsables: { id: string; nombre: string }[],
  asignaciones: { usuarioId: string; usuarioNombre: string }[],
): OpcionReparto[] => {
  const porId = new Map<string, OpcionReparto>(
    responsables.map((r) => [r.id, { id: r.id, nombre: r.nombre, vigente: true }]),
  )

  for (const a of asignaciones) {
    if (porId.has(a.usuarioId)) continue
    porId.set(a.usuarioId, { id: a.usuarioId, nombre: a.usuarioNombre, vigente: false })
  }

  return [...porId.values()]
}

/**
 * Los candidatos a recibir un concepto: cualquiera del estudio que siga activo (ORR-303).
 *
 * Antes se intersectaba con los responsables del cliente, y eso anulaba el sentido de la función
 * —delegar un impuesto sin delegar el cliente—. El filtro por `activo` sí se queda: bloquear a un
 * usuario no borra sus asignaciones, y asignarle algo que nunca va a ver no le sirve a nadie.
 */
export const candidatosDeReparto = (
  usuarios: { id: string; nombre: string | null; email: string; activo: boolean }[],
): { id: string; nombre: string; email: string }[] => {
  const candidatos: { id: string; nombre: string; email: string }[] = []

  for (const u of usuarios) {
    if (u.activo) candidatos.push({ id: u.id, nombre: u.nombre || u.email, email: u.email })
  }

  return candidatos
}

/**
 * A quién avisarle de una asignación, o `null` si no corresponde avisar.
 *
 * Nunca auto-notificar: repartirse un concepto a uno mismo no es novedad para nadie.
 */
export const destinatarioDeNotificacion = (
  usuarioEmail: string | undefined,
  actorEmail: string | undefined,
): string | null => {
  if (!usuarioEmail || !actorEmail) return null
  if (usuarioEmail === actorEmail) return null
  return `user:${usuarioEmail}`
}
